import { env } from "../config/env";
import { createClient } from "./http";

const restaurantAppApi = createClient(env.RESTAURANT_APP_BASE_URL || env.AUTH_BASE_URL);
const catalogApi = createClient(env.CATALOG_BASE_URL || env.AUTH_BASE_URL);
const authApi = createClient(env.AUTH_BASE_URL);
const warehouseApi = createClient(env.WAREHOUSE_BASE_URL || env.AUTH_BASE_URL);
const userApi = createClient(env.USER_BASE_URL || env.AUTH_BASE_URL);
const orderApi = createClient(env.ORDER_BASE_URL || env.AUTH_BASE_URL);
const reportApi = createClient(env.REPORT_BASE_URL || env.AUTH_BASE_URL);

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export function rowsOf(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  if (Array.isArray(res?.data?.restaurants)) return res.data.restaurants;
  if (Array.isArray(res?.data?.requests)) return res.data.requests;
  if (Array.isArray(res?.data?.employees)) return res.data.employees;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.result)) return res.result;
  if (Array.isArray(res?.restaurants)) return res.restaurants;
  if (Array.isArray(res?.requests)) return res.requests;
  if (Array.isArray(res?.employees)) return res.employees;
  if (Array.isArray(res?.orders)) return res.orders;
  return [];
}

export function unwrap(res, fallback = {}) {
  if (res?.data && !Array.isArray(res.data)) return res.data;
  return res || fallback;
}

export const restaurantAdminService = {
  // Restaurant app service: admin restaurant management
  listRestaurants(params = {}) {
    return restaurantAppApi.get("/api/admin/restaurant-app/restaurants", { params: cleanParams(params) });
  },
  createRestaurant(payload) {
    return restaurantAppApi.post("/api/admin/restaurant-app/restaurants", payload);
  },
  getRestaurant(id) {
    return restaurantAppApi.get(`/api/admin/restaurant-app/restaurants/${id}`);
  },
  updateRestaurant(id, payload) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/restaurants/${id}`, payload);
  },
  setRestaurantStatus(id, status) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/restaurants/${id}`, { status });
  },
  setRestaurantOpenStatus(id, isOpen) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/restaurants/${id}`, { isOpen });
  },
  setRestaurantPermanentClose(id, payload = {}) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/restaurants/${id}/permanent-close`, payload);
  },

  // Auth service: public restaurant registrations awaiting admin approval
  listRestaurantRegistrationRequests(params = {}) {
    return authApi.get("/api/restaurant-auth/admin/restaurant-registration-requests", { params: cleanParams(params) });
  },
  getRestaurantRegistrationRequest(id) {
    return authApi.get(`/api/restaurant-auth/admin/restaurant-registration-requests/${id}`);
  },
  updateRestaurantRegistrationRequest(id, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurant-registration-requests/${id}`, payload);
  },
  approveRestaurantRegistrationRequest(id, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurant-registration-requests/${id}/approve`, payload);
  },
  rejectRestaurantRegistrationRequest(id, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurant-registration-requests/${id}/reject`, payload);
  },

  // Auth service: restaurant employees belong to auth-service only

  listAllRestaurantEmployees(params = {}) {
    return authApi.get("/api/restaurant-auth/admin/employees", { params: cleanParams(params) });
  },
  updateRestaurantEmployee(employeeId, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}`, payload);
  },
  createRestaurantEmployee(restaurantId, payload) {
    return authApi.post(`/api/restaurant-auth/admin/restaurants/${restaurantId}/employees`, payload);
  },
  listRestaurantEmployees(restaurantId) {
    return authApi.get(`/api/restaurant-auth/admin/restaurants/${restaurantId}/employees`);
  },
  setRestaurantEmployeeStatus(employeeId, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}/status`, payload);
  },
  activateRestaurantEmployee(employeeId) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}/activate`);
  },
  deactivateRestaurantEmployee(employeeId) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}/deactivate`);
  },
  blockRestaurantEmployee(employeeId) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}/block`);
  },

  reviewRestaurantEmployeeOnboarding(employeeId, payload) {
    return authApi.patch(`/api/restaurant-auth/admin/restaurants/employees/${employeeId}/approval`, payload);
  },


  // Restaurant food taxonomy APIs
  uploadFoodImage(file) {
    const formData = new FormData();
    formData.append("image", file);
    return catalogApi.post("/api/restaurant-food/admin/upload-image", formData);
  },
listFoodCategories(params = {}) {
  return catalogApi.get("/api/restaurant-food/admin/categories", { params: cleanParams(params) });
},
createFoodCategory(payload) {
  return catalogApi.post("/api/restaurant-food/admin/categories", payload);
},
updateFoodCategory(categoryId, payload) {
  return catalogApi.patch(`/api/restaurant-food/admin/categories/${categoryId}`, payload);
},
setFoodCategoryStatus(categoryId, isActive) {
  return catalogApi.patch(`/api/restaurant-food/admin/categories/${categoryId}/status`, { isActive });
},
deleteFoodCategory(categoryId) {
  return catalogApi.del(`/api/restaurant-food/admin/categories/${categoryId}`);
},
listFoodSubcategories(params = {}) {
  return catalogApi.get("/api/restaurant-food/admin/subcategories", { params: cleanParams(params) });
},
createFoodSubcategory(payload) {
  return catalogApi.post("/api/restaurant-food/admin/subcategories", payload);
},
updateFoodSubcategory(subcategoryId, payload) {
  return catalogApi.patch(`/api/restaurant-food/admin/subcategories/${subcategoryId}`, payload);
},
setFoodSubcategoryStatus(subcategoryId, isActive) {
  return catalogApi.patch(`/api/restaurant-food/admin/subcategories/${subcategoryId}/status`, { isActive });
},
deleteFoodSubcategory(subcategoryId) {
  return catalogApi.del(`/api/restaurant-food/admin/subcategories/${subcategoryId}`);
},
  listFoodTags(params = {}) {
    return catalogApi.get("/api/restaurant-food/admin/tags", { params: cleanParams(params) });
  },
  createFoodTag(payload) {
    return catalogApi.post("/api/restaurant-food/admin/tags", payload);
  },
  setFoodTagStatus(tagId, isActive) {
    return catalogApi.patch(`/api/restaurant-food/admin/tags/${tagId}/status`, { isActive });
  },

  // Food catalog + restaurant mapping APIs
  listFoodItems(params = {}) {
    return catalogApi.get("/api/restaurant-food/admin/items", { params: cleanParams(params) });
  },
  getFoodItem(foodItemId) {
    return catalogApi.get(`/api/restaurant-food/admin/items/${foodItemId}`);
  },
  createFoodItem(payload) {
    return catalogApi.post("/api/restaurant-food/admin/items", payload);
  },
  manualAddFood(payload) {
    return catalogApi.post("/api/restaurant-food/admin/items", payload);
  },
  updateFoodItem(foodItemId, payload) {
    return catalogApi.patch(`/api/restaurant-food/admin/items/${foodItemId}`, payload);
  },
  setFoodItemStatus(foodItemId, isActive) {
    return catalogApi.patch(`/api/restaurant-food/admin/items/${foodItemId}/status`, { isActive });
  },
  deleteFoodItem(foodItemId) {
    return catalogApi.del(`/api/restaurant-food/admin/items/${foodItemId}`);
  },

  // Approved restaurant combos shown inside Food Catalog
  listFoodCombos(params = {}) {
    return catalogApi.get("/api/restaurant-food/admin/combos", { params: cleanParams(params) });
  },
  getFoodCombo(comboId) {
    return catalogApi.get(`/api/restaurant-food/admin/combos/${comboId}`);
  },
  updateFoodCombo(comboId, payload) {
    return catalogApi.patch(`/api/restaurant-food/admin/combos/${comboId}`, payload);
  },
  setFoodComboStatus(comboId, payload) {
    return catalogApi.patch(`/api/restaurant-food/admin/combos/${comboId}/status`, payload);
  },
  deleteFoodCombo(comboId) {
    return catalogApi.del(`/api/restaurant-food/admin/combos/${comboId}`);
  },
  assignFoodToRestaurants(foodItemId, payload) {
    return catalogApi.post(`/api/restaurant-food/admin/items/${foodItemId}/assign-restaurants`, payload);
  },
  listRestaurantFoodItems(restaurantId, params = {}) {
    return catalogApi.get(`/api/restaurant-food/admin/restaurants/${restaurantId}/items`, { params: cleanParams(params) });
  },
  setRestaurantFoodAvailability(restaurantId, foodItemId, payload) {
    return catalogApi.patch(`/api/restaurant-food/admin/restaurants/${restaurantId}/items/${foodItemId}/availability`, payload);
  },

  // Restaurant app service: product/food approval requests
  listFoodRequests(params = {}) {
    return restaurantAppApi.get("/api/admin/restaurant-app/food-requests", { params: cleanParams(params) });
  },
  getFoodRequest(id) {
    return restaurantAppApi.get(`/api/admin/restaurant-app/food-requests/${id}`);
  },
  approveFoodRequest(id, payload) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/food-requests/${id}/approve`, payload);
  },
  rejectFoodRequest(id, payload) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/food-requests/${id}/reject`, payload);
  },


  // Restaurant app service: restaurant business profile approval requests
  listProfileRequests(params = {}) {
    return restaurantAppApi.get("/api/admin/restaurant-app/profile-requests", { params: cleanParams(params) });
  },
  getProfileRequest(id) {
    return restaurantAppApi.get(`/api/admin/restaurant-app/profile-requests/${id}`);
  },
  approveProfileRequest(id, payload = {}) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/profile-requests/${id}/approve`, payload);
  },
  rejectProfileRequest(id, payload = {}) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/profile-requests/${id}/reject`, payload);
  },

  // Restaurant app service: combo approval requests
  listComboRequests(params = {}) {
    return restaurantAppApi.get("/api/admin/restaurant-app/combo-requests", { params: cleanParams(params) });
  },
  getComboRequest(id) {
    return restaurantAppApi.get(`/api/admin/restaurant-app/combo-requests/${id}`);
  },
  approveComboRequest(id, payload) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/combo-requests/${id}/approve`, payload);
  },
  rejectComboRequest(id, payload) {
    return restaurantAppApi.patch(`/api/admin/restaurant-app/combo-requests/${id}/reject`, payload);
  },

  // Food orders: admin endpoints owned by order-service
  listFoodOrders(params = {}) {
    return orderApi.get("/api/restaurant-orders/admin", { params: cleanParams(params) });
  },
  getFoodOrder(orderId) {
    return orderApi.get(`/api/restaurant-orders/admin/${orderId}`);
  },
  getFoodOrderManualAssignmentOptions(orderId, params = {}) {
    return orderApi.get(`/api/restaurant-orders/admin/${orderId}/manual-assignment-options`, { params: cleanParams({ includeUnavailable: true, ...params }) });
  },
  manualAssignFoodOrder(orderId, payload = {}) {
    return orderApi.post(`/api/restaurant-orders/admin/${orderId}/manual-assign`, payload);
  },

  cancelFoodOrder(orderId, payload = {}) {
    return orderApi.post(`/api/restaurant-orders/admin/${orderId}/cancel`, payload);
  },



  // Restaurant coupons: owned by order-service food order module
  listRestaurantCoupons(params = {}) {
    return orderApi.get("/api/restaurant-orders/admin/coupons", { params: cleanParams(params) });
  },
  createRestaurantCoupon(payload) {
    return orderApi.post("/api/restaurant-orders/admin/coupons", payload);
  },
  updateRestaurantCoupon(id, payload) {
    return orderApi.patch(`/api/restaurant-orders/admin/coupons/${id}`, payload);
  },
  setRestaurantCouponActive(id, isActive) {
    return orderApi.patch(`/api/restaurant-orders/admin/coupons/${id}/active`, { isActive });
  },
  approveRestaurantCoupon(id) {
    return orderApi.post(`/api/restaurant-orders/admin/coupons/${id}/approve`, {});
  },
  rejectRestaurantCoupon(id, reason) {
    return orderApi.post(`/api/restaurant-orders/admin/coupons/${id}/reject`, { reason });
  },
  deleteRestaurantCoupon(id) {
    return orderApi.del(`/api/restaurant-orders/admin/coupons/${id}`);
  },
  listRestaurantCouponUsages(id, params = {}) {
    return orderApi.get(`/api/restaurant-orders/admin/coupons/${id}/usages`, { params: cleanParams(params) });
  },

  // Reports
  getRestaurantSummary(params = {}) {
    return restaurantAppApi.get("/api/admin/restaurant-app/reports/restaurants/summary", { params: cleanParams(params) });
  },
  getRestaurantSales(params = {}) {
    return reportApi.get("/api/reports/restaurants/restaurants", { params: cleanParams(params) });
  },
  getFoodItemSales(params = {}) {
    return reportApi.get("/api/reports/restaurants/items", { params: cleanParams(params) });
  },
  getRestaurantFinancialReport(params = {}) {
    return orderApi.get("/api/restaurant-orders/admin/reports", { params: cleanParams(params) });
  },

  // Settings stay in existing admin/settings service
  getBusinessMode() {
    return warehouseApi.get("/api/admin/restaurants/settings/business-mode");
  },
  saveBusinessMode(payload) {
    return warehouseApi.put("/api/admin/restaurants/settings/business-mode", payload);
  },
};
